import type { JobDef, JobId } from '../types';

/**
 * Jobs the tribe can be assigned to. Rates are per worker per second before
 * any modifiers. A job whose `inputs` cannot be met runs at reduced efficiency
 * rather than stopping outright.
 */
export const JOBS: JobDef[] = [
  // --- Age I ---------------------------------------------------------------
  {
    id: 'forager',
    name: 'Forage',
    workers: 'Foragers',
    glyph: 'leaf',
    age: 0,
    description: 'Walk the ground and take what grows on it.',
    outputs: { forage: 0.5 },
  },
  {
    id: 'knapper',
    name: 'Knap',
    workers: 'Knappers',
    glyph: 'stone',
    age: 0,
    description: 'Strike stone against stone until one of them has an edge.',
    outputs: { flint: 0.25 },
  },
  {
    id: 'tinderer',
    name: 'Gather Tinder',
    workers: 'Tinderers',
    glyph: 'grass',
    age: 0,
    description: 'Collect and keep dry the things that will one day catch.',
    outputs: { tinder: 0.3 },
  },
  {
    id: 'elder',
    name: 'Remember',
    workers: 'Elders',
    glyph: 'eye',
    age: 0,
    description: 'Sit apart, watch, and hold on to what worked. Slow, and worth it.',
    outputs: { insight: 0.02 },
  },

  // --- Age II --------------------------------------------------------------
  {
    id: 'firekeeper',
    name: 'Keep the Fire',
    workers: 'Firekeepers',
    glyph: 'flame',
    age: 1,
    description: 'Feed the coal, shield it from rain, and never once look away.',
    outputs: { ember: 0.2 },
    inputs: { tinder: 0.1 },
  },
  {
    id: 'charburner',
    name: 'Burn Charcoal',
    workers: 'Charburners',
    glyph: 'coal',
    age: 1,
    description: 'Bury the burn so it smoulders instead of blazing.',
    outputs: { charcoal: 0.1 },
    inputs: { ember: 0.05, tinder: 0.2 },
  },

  // --- Age III -------------------------------------------------------------
  {
    id: 'hunter',
    name: 'Hunt',
    workers: 'Hunters',
    glyph: 'spear',
    age: 2,
    description: 'Follow, wait, and close. Brings back meat, hide and bone together.',
    outputs: { meat: 0.35, hide: 0.08, bone: 0.06 },
  },
  {
    id: 'tanner',
    name: 'Tan Hides',
    workers: 'Tanners',
    glyph: 'hide',
    age: 2,
    description: 'Scrape, smoke and work the skin until it stops being a corpse.',
    outputs: { leather: 0.1 },
    inputs: { hide: 0.12, ember: 0.04 },
  },

  // --- Age IV --------------------------------------------------------------
  {
    id: 'woodcutter',
    name: 'Cut Timber',
    workers: 'Woodcutters',
    glyph: 'tree',
    age: 3,
    description: 'Fell, limb and drag. The heaviest work the tribe has yet invented.',
    outputs: { timber: 0.4 },
  },
  {
    id: 'weaver',
    name: 'Twist Fibre',
    workers: 'Weavers',
    glyph: 'thread',
    age: 3,
    description: 'Ret, strip and twist until a handful of nettle can take a person’s weight.',
    outputs: { fiber: 0.3 },
  },

  // --- Age V ---------------------------------------------------------------
  {
    id: 'painter',
    name: 'Grind Ochre',
    workers: 'Painters',
    glyph: 'hand',
    age: 4,
    description: 'Dig the red earth and grind it to a powder that will hold on stone.',
    outputs: { ochre: 0.25 },
  },
  {
    id: 'shaman',
    name: 'Keep the Lore',
    workers: 'Shamans',
    glyph: 'spiral',
    age: 4,
    description: 'Put knowledge on the wall so the tribe stops relearning what it knew.',
    outputs: { insight: 0.15 },
    inputs: { ochre: 0.08 },
  },

  // --- Age VI --------------------------------------------------------------
  {
    id: 'planter',
    name: 'Plant',
    workers: 'Planters',
    glyph: 'grain',
    age: 5,
    description: 'Put the seed back in the ground on purpose and wait for it to answer.',
    outputs: { grain: 0.6 },
  },
  {
    id: 'digger',
    name: 'Dig Clay',
    workers: 'Diggers',
    glyph: 'clay',
    age: 5,
    description: 'Cut clay from the riverbank and carry it up wet.',
    outputs: { clay: 0.35 },
  },
  {
    id: 'potter',
    name: 'Fire Pots',
    workers: 'Potters',
    glyph: 'pot',
    age: 5,
    description: 'Shape, dry, and fire. What comes out will outlive everyone who made it.',
    outputs: { pottery: 0.08 },
    inputs: { clay: 0.15, ember: 0.05 },
  },
];

export const JOBS_BY_ID: Record<JobId, JobDef> = Object.fromEntries(
  JOBS.map((job) => [job.id, job]),
);

export const JOB_IDS: JobId[] = JOBS.map((job) => job.id);

export function getJob(id: JobId): JobDef | undefined {
  return JOBS_BY_ID[id];
}
